import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
// Removed FaVerified from this line
import { FaChartBar, FaTrophy, FaExclamationTriangle, FaInbox, FaTimes } from "react-icons/fa";
// Added MdVerified from the Material Design library
import { MdVerified } from "react-icons/md"; 
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import constantObj from "../constant/constant";

const initialProofData = { Prom: [], Live: [] };
const API_URL = `${constantObj?.API_URL_DEV}/admin/proof`;

export default function ProofOfWorkSection() {
  const [proofs, setProofs] = useState(initialProofData);
  const [selectedAccount, setSelectedAccount] = useState("Live");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [activeProof, setActiveProof] = useState(null);

  useEffect(() => {
    const fetchProofs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();
        const structuredData = { Prom: [], Live: [] };
        if (result.data && Array.isArray(result.data)) {
          result.data.forEach((proof) => {
            const category = proof.category || "Live";
            if (structuredData[category]) structuredData[category].push(proof);
          });
        }
        setProofs(structuredData);
      } catch (err) {
        setError("Failed to load proof data. Please try again.");
        setProofs(initialProofData);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProofs();
  }, []);

  const currentProofs = proofs[selectedAccount] || [];
  const openModal = (proof) => { setActiveProof(proof); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setActiveProof(null); };

  const tabs = [
    { key: "Live", label: "Live Account Results", icon: <FaChartBar className="text-sm" /> },
    { key: "Prom", label: "Prop Firm Passed", icon: <FaTrophy className="text-sm" /> },
  ];

  return (
    <section className="py-24 px-6" style={{ background: "var(--uea-black)" }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-mono-code text-green-400 tracking-widest uppercase">// Verified Results</span>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="font-orbitron font-black text-3xl sm:text-4xl text-white mt-4">
            Proof of{" "}
            <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Real Profits
            </span>
          </motion.h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto text-sm">
            Live verified trading results and prop firm challenge passes. No simulations — all real money.
          </p>
        </div>

        <div className="flex justify-center gap-4 mb-10">
          {tabs.map((tab) => (
            <motion.button key={tab.key} onClick={() => setSelectedAccount(tab.key)}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300"
              style={selectedAccount === tab.key
                ? { background: "linear-gradient(135deg, #22c55e, #3b82f6)", color: "white", boxShadow: "0 0 20px rgba(34,197,94,0.3)" }
                : { background: "var(--uea-card)", border: "1px solid rgba(59,130,246,0.15)", color: "#94a3b8" }}>
              {tab.icon} {tab.label}
            </motion.button>
          ))}
        </div>

        {isLoading && (
          <div className="text-center py-20">
            <motion.div className="w-12 h-12 rounded-full border-2 mx-auto mb-4"
              style={{ borderTopColor: "#22c55e", borderRightColor: "#3b82f6", borderBottomColor: "transparent", borderLeftColor: "transparent" }}
              animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
            <p className="text-slate-400 font-mono-code text-sm">Loading proof data...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-12 text-red-400 text-sm border rounded-xl flex items-center justify-center gap-2"
            style={{ borderColor: "rgba(239,68,68,0.2)", background: "rgba(239,68,68,0.03)" }}>
            <FaExclamationTriangle /> {error}
          </div>
        )}

        {!isLoading && currentProofs.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="flex justify-center mb-4" style={{ color: "#475569" }}>
              <FaInbox className="text-5xl" />
            </div>
            <p className="text-slate-400">No results available for this category yet.</p>
          </div>
        )}

        {!isLoading && currentProofs.length > 0 && (
          <Swiper modules={[Navigation, Pagination]} spaceBetween={20}
            breakpoints={{ 640: { slidesPerView: 1 }, 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
            navigation pagination={{ clickable: true }} className="pb-12">
            {currentProofs.map((proof) => (
              <SwiperSlide key={proof._id || proof.id}>
                <motion.div className="rounded-2xl overflow-hidden border cursor-pointer group"
                  style={{ borderColor: "rgba(59,130,246,0.15)", background: "var(--uea-card)" }}
                  whileHover={{ y: -6, borderColor: "rgba(34,197,94,0.4)" }}
                  onClick={() => openModal(proof)}>
                  <div className="relative overflow-hidden h-52 bg-black">
                    {proof.media && proof.media.length > 0 ? (
                      proof.media[0].type === "video" ? (
                        <video src={`${constantObj?.API_URL_DEV}/${proof.media[0].path}`} muted
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <img src={proof.media.length > 1 ? proof.media[0].path : `${constantObj?.API_URL_DEV}/${proof.media[0].path}`}
                          alt={proof.description} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ color: "#1e3a5f" }}>
                        <FaChartBar className="text-5xl" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">View Full Proof →</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-orbitron text-white text-sm font-bold">{proof.description}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      {/* Fixed: Replaced FaVerified with MdVerified */}
                      <motion.div
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                      >
                        <MdVerified className="text-sm text-green-400" />
                      </motion.div>
                      <span className="text-xs text-slate-400 font-mono-code">Verified Result</span>
                    </div>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && activeProof && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(5,10,20,0.95)", backdropFilter: "blur(12px)" }}
            onClick={closeModal}>
            <motion.div initial={{ scale: 0.8, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 30 }}
              className="relative rounded-2xl border overflow-hidden max-w-lg w-full"
              style={{ background: "var(--uea-card)", borderColor: "rgba(34,197,94,0.3)" }}
              onClick={(e) => e.stopPropagation()}>
              <button onClick={closeModal}
                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center text-white"
                style={{ background: "rgba(0,0,0,0.5)" }}>
                <FaTimes />
              </button>
              {activeProof.media && activeProof.media.length > 1 ? (
                <Swiper modules={[Navigation, Pagination]} spaceBetween={10} navigation pagination={{ clickable: true }}>
                  {activeProof.media.map((item, index) => (
                    <SwiperSlide key={index}>
                      {item.type === "image"
                        ? <img src={`${constantObj?.API_URL_DEV}/${item.path}`} alt={`Slide ${index + 1}`} className="w-full max-h-[70vh] object-contain" />
                        : <video src={`${constantObj?.API_URL_DEV}/${item.path}`} controls muted className="w-full max-h-[70vh]" />}
                      {item.level && <p className="text-center text-white text-sm py-3 font-semibold capitalize">{item.level}</p>}
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : activeProof.media && activeProof.media.length === 1 ? (
                activeProof.media[0].type === "image"
                  ? <img src={`${constantObj?.API_URL_DEV}/${activeProof.media[0].path}`} alt={activeProof.description} className="w-full max-h-[70vh] object-contain" />
                  : <video src={`${constantObj?.API_URL_DEV}/${activeProof.media[0].path}`} controls muted className="w-full max-h-[70vh]" />
              ) : null}
              <div className="p-5 border-t" style={{ borderColor: "rgba(34,197,94,0.1)" }}>
                <h3 className="font-orbitron text-white text-sm font-bold">{activeProof.description}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}